'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';
import Image from 'next/image';

import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { useInitialMount } from '@core/Hooks/UseInitialMount';
import { TickSuccess } from '@gds/Icons/Paths/TickSuccess';
import { Typography } from '@gds/Typography/Typography';
import { isModalOpenAtom } from '@modal/Atoms/Modal.atom';

import styles from './MotorwayModal.module.css';

interface MotorwayModalProps {
  redirectTimerCopy?: string | null;
  redirectSubheader?: string | null;
  redirectUspOne?: string | null;
  redirectUspTwo?: string | null;
  redirectUspThree?: string | null;
  redirectErrorMessage?: string | null;
  redirectLogo?: string | null;
  reg?: string;
  modalKey?: number;
}

export const MotorwayModal = ({
  redirectTimerCopy,
  redirectSubheader,
  redirectUspOne,
  redirectUspTwo,
  redirectUspThree,
  redirectErrorMessage,
  redirectLogo,
  reg,
  modalKey,
}: MotorwayModalProps) => {
  const tenant = useAtomValue(tenantAtom);

  const redirectUrls: Record<string, string> = {
    rac: `https://motorway.co.uk/partner/drivecouk?utm_source=drivecouk&utm_medium=affiliate&utm_campaign=sellmycar&vrm=${reg}`,
    default: `https://motorway.co.uk/partner/drivecouk?utm_source=drivecouk&utm_medium=affiliate&utm_campaign=sellmycar&vrm=${reg}&vrm_redirect=true`,
  };

  const usps = [redirectUspOne, redirectUspTwo, redirectUspThree];
  const redirectUrl = (tenant && redirectUrls?.[tenant]) || redirectUrls.default;

  const [countdown, setCountdown] = useState(3);
  const isModalOpen = useAtomValue(isModalOpenAtom);
  const isInitialMount = useInitialMount();

  useEffect(() => {
    if (!isInitialMount) {
      setCountdown(3);
    }
  }, [modalKey]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown(prevCountdown => prevCountdown - 1);
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
    if (isModalOpen) {
      if (window.innerWidth < 768) {
        window.location.href = redirectUrl;
        return;
      }

      window.open(redirectUrl, '_blank');
    }
  }, [countdown]);

  return (
    <div className={styles.modalWrapper}>
      <Typography variant={'h3'} className={styles.modalTitle}>
        {redirectTimerCopy} {countdown}
        {'...'}
      </Typography>
      <Typography variant={'h6'}>{redirectSubheader}</Typography>
      <ul className={styles.uspList}>
        {usps.map(usp => {
          if (usp) {
            return (
              <li key={usp} className={styles.uspListRow}>
                <TickSuccess />
                {usp}
              </li>
            );
          }
          return null;
        })}
      </ul>
      {countdown < 1 && (
        <p className={styles.redirectError}>
          <a href={redirectUrl} rel="noopener noreferrer" target="_blank">
            {redirectErrorMessage}
          </a>
        </p>
      )}
      {redirectLogo && (
        <Image
          src={redirectLogo}
          alt="Motorway Logo"
          width={225}
          height={60}
          className={styles.logo}
        />
      )}
    </div>
  );
};
